/**
 * CallToActionTab Component
 * Manages Call to Action configurations with QR code generation
 */

import React, { useState } from "react";
import PromoCodeLookupStatus from "../../common/PromoCodeLookupStatus";
import { useQuery } from "@tanstack/react-query";
import {
  PlusIcon,
  QrCodeIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import {
  useEventCTAs,
  useCreateEventCTA,
  useGenerateCTAQRCode,
} from "../../../hooks/useEventCTAs";
import {
  ctaOmitsDiscount,
  getDefaultBaseUrl,
  getEventCTABaseVariant,
} from "../../../services/eventCTA.service";
import { INSURANCE_NO_VOUCHER_NOTE } from "../../../services/ctaClassroom";
import { supabase } from "../../../services/supabase";
import { SUBSCRIPTION_CTA_BASE_URL } from "../../../types/event.types";
import type { Event, CTAType, RewardType } from "../../../types/event.types";
import type { SalesPosition } from "../../../types/sales.types";
import { DEFAULT_TIMEZONE, US_TIMEZONES } from "../../common/TimezoneSelector";
import LoadingSpinner from "../../common/LoadingSpinner";
import Button from "../../common/Button";
import { useToast } from "../../ui/Toast";
import CTACard from "../CTACard";
import EventCTAInitPanel from "../EventCTAInitPanel";
import {
  canEditEventCTAs,
  ceilingProblem,
  getEventStateOffer,
  previewEventCTADefaults,
} from "../../../services/eventCTAInit.service";

interface CallToActionTabProps {
  event: Event;
}

const CallToActionTab: React.FC<CallToActionTabProps> = ({ event }) => {
  const { showToast } = useToast();
  const [showAddCTA, setShowAddCTA] = useState(false);
  const [newCTAName, setNewCTAName] = useState("");
  const [newCTAType, setNewCTAType] = useState<CTAType | "">("");

  // Reward configuration
  const [rewardType, setRewardType] = useState<RewardType>("voucher");
  // Insurance-state venue (migration 2026-10-09-410): subscription CTAs carry no voucher. The database enforces it
  // (_eib_insurance_cta_no_voucher); the form just stops offering it.
  const { data: ctaBaseVariant } = useQuery({
    queryKey: [
      "event-cta-base-variant",
      event.id,
      event.type,
      event.location_state,
    ],
    queryFn: () => getEventCTABaseVariant(event.id),
  });
  // Mig 1480: a gun show / range night CTA may give at most the state's Event in a Box offer.
  const eibOfferType =
    event.type === "gun_show" || event.type === "range_night";
  const { data: stateOffer } = useQuery({
    queryKey: [
      "event-state-offer",
      event.id,
      event.type,
      event.location_state,
      event.state,
    ],
    queryFn: () => getEventStateOffer(event.id),
    enabled: eibOfferType,
    staleTime: 60_000,
  });
  const stateCeiling =
    eibOfferType &&
    stateOffer?.offer_status === "voucher" &&
    Number(stateOffer.ceiling) > 0
      ? Number(stateOffer.ceiling)
      : null;
  const noVoucherHere = ctaOmitsDiscount(
    { cta_type: newCTAType || "subscription" },
    ctaBaseVariant,
  );
  React.useEffect(() => {
    if (noVoucherHere && rewardType === "voucher") {
      setRewardType("none");
      setVoucherAmount("");
    }
  }, [noVoucherHere, rewardType]);
  const [selectedOfferId, setSelectedOfferId] = useState<string>("");
  const [voucherAmount, setVoucherAmount] = useState("20");
  const [voucherExpiryDays, setVoucherExpiryDays] = useState("30");
  // Default checked: new voucher CTAs expire vouchers at the event's end.
  const [voucherExpiresAtEventEnd, setVoucherExpiresAtEventEnd] =
    useState(true);
  const [voucherStartDate, setVoucherStartDate] = useState("");
  const [voucherEndDate, setVoucherEndDate] = useState("");

  // URL configuration
  // Event CTAs default to NOT including the state parameter (ste=) — an event's
  // audience isn't state-scoped the way an org/location CTA is. Operators can opt
  // in via the checkbox. (Org CTAs, which key ste= off the location state, keep
  // their own default in the org CTA form.)
  const [includeStateParam, setIncludeStateParam] = useState(false);
  // Off by default: an existing CTA's URL must not change because this option now exists.
  const [useStateDefaults, setUseStateDefaults] = useState(false);
  // Off by default (owner 2026-09-29): an operator must opt a CTA in to singlevoucher=1.
  const [singleVoucher, setSingleVoucher] = useState(false);
  const [useMemberSignup, setUseMemberSignup] = useState(false);
  const [displayAwayPlans, setDisplayAwayPlans] = useState(false);

  // Subscription CTA parameters
  const [ctaParameters, setCtaParameters] = useState<Record<string, string>>(
    {},
  );

  // Other CTA - base URL
  const [otherBaseUrl, setOtherBaseUrl] = useState("");

  // R2B (2026-05-17): rep attribution. Multi-select per audit Decision 5
  // — operator can create N CTAs (one per selected rep) in one save.
  // Empty array = a single un-attributed CTA (the generic fallback
  // pattern). selectedSalesPositionId is the LEGACY single-value state
  // still threaded into the create handler when no multi-rep is picked
  // (back-compat for the existing single-rep flow).
  const [selectedSalesPositionId, setSelectedSalesPositionId] =
    useState<string>("");
  const [selectedRepIds, setSelectedRepIds] = useState<string[]>([]);
  const [perRepResults, setPerRepResults] = useState<
    { repId: string; repName: string; ok: boolean; error?: string }[] | null
  >(null);

  // Subscription CTA field definitions
  const SUBSCRIPTION_FIELDS = [
    {
      key: "affid",
      label: "Promoter Affiliate ID",
      placeholder: "e.g., ABC123",
    },
    { key: "promo", label: "Promo Code", placeholder: "e.g., SUMMER2025" },
    { key: "altprice", label: "Alt Price", placeholder: "e.g., 10.99" },
    { key: "utm_source", label: "UTM Source", placeholder: "e.g., event" },
    { key: "utm_term", label: "UTM Term", placeholder: "e.g., signup" },
    {
      key: "utm_campaign",
      label: "UTM Campaign",
      placeholder: "e.g., spring_promo",
    },
    { key: "utm_medium", label: "UTM Medium", placeholder: "e.g., qrcode" },
    {
      key: "utm_content",
      label: "UTM Content",
      placeholder: "e.g., banner_ad",
    },
  ];

  // Fetch CTAs
  const { data: ctas, isLoading, error, refetch } = useEventCTAs(event.id);

  // Get current user's access
  // Who may change this event's CTAs: the same database helper the CTA RLS policy and the preparation
  // RPCs use (audit H2). getUserEventAccess was wrong for this surface in both directions -- it hid
  // editing from the event's creator and showed it to the assigned MSR, whose writes RLS refuses.
  const { data: canEdit = false, isLoading: isLoadingAccess } = useQuery({
    queryKey: ["can-edit-event-ctas", event.id],
    queryFn: () => canEditEventCTAs(event.id),
  });
  // The prepared CTA (if any): its utm_term is derived server-side, so its card shows it read-only.
  // Same query key as EventCTAInitPanel, so this shares that request.
  const { data: prepared } = useQuery({
    queryKey: ["event-cta-init", event.id],
    queryFn: () => previewEventCTADefaults(event.id),
    enabled: canEdit,
    refetchOnWindowFocus: false,
  });
  const eventEnded =
    !!event.end_time && new Date(event.end_time).getTime() < Date.now();

  // Create CTA mutation
  const createCTA = useCreateEventCTA();
  // UC-R2B.1 bug #2: auto-generate each CTA's QR right after create (like the
  // clone path) so a freshly created/recreated rep CTA immediately carries its
  // rep + partner label — no stale/blank QR until a manual "Regenerate QRs".
  const generateQR = useGenerateCTAQRCode();

  // Fetch promoter's promo_code and affiliate_id if event has a promoter
  const { data: promoterData } = useQuery({
    queryKey: ["promoter-data", event.promoter_id],
    queryFn: async () => {
      if (!event.promoter_id) {
        return null;
      }
      const { data, error } = await supabase
        .from("organizations")
        .select("promo_code, affiliate_id")
        .eq("id", event.promoter_id)
        .single();

      if (error) {
        console.warn("Error fetching promoter data:", error);
        return null;
      }
      return data || null;
    },
    enabled: !!event.promoter_id,
  });
  const promoterPromoCode = promoterData?.promo_code || null;
  const promoterAffiliateId = promoterData?.affiliate_id || null;

  // Fetch available offers (active offers that can be linked to CTAs)
  const { data: availableOffers, isLoading: isLoadingOffers } = useQuery({
    // Distinct key per data shape (2026-10-16): the same key held different rows/columns on other
    // screens, and with the 5-minute staleTime whichever screen fetched first won.
    queryKey: ["available-offers", "cta-tab"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("offers")
        .select(
          "id, offer_code, name, description, offer_type, value_type, value_amount",
        )
        .eq("is_active", true)
        .order("name");

      if (error) {
        console.warn("Error fetching offers:", error);
        return [];
      }
      return data || [];
    },
  });

  // Fetch active sales positions for rep attribution
  const { data: salesPositions } = useQuery({
    // Distinct key per data shape (2026-10-16): the same key held different rows/columns on other
    // screens, and with the 5-minute staleTime whichever screen fetched first won.
    queryKey: ["sales-positions-active", "cta-tab-select"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sales_positions")
        .select("id, title, assigned_name, position_type")
        .eq("is_active", true)
        .order("assigned_name", { ascending: true, nullsFirst: false });

      if (error) {
        console.warn("Error fetching sales positions:", error);
        return [];
      }
      return data || [];
    },
  });

  // Check if event has required state
  const hasRequiredState = event.state && event.state.length === 2;

  // Helper to extract date portion from ISO string
  const getDatePart = (iso?: string) => {
    if (!iso) {
      return "";
    }
    return iso.split("T")[0];
  };

  const resetForm = () => {
    setShowAddCTA(false);
    setNewCTAName("");
    setNewCTAType("");
    setRewardType("voucher");
    setSelectedOfferId("");
    setVoucherAmount("20");
    setVoucherExpiryDays("30");
    setVoucherExpiresAtEventEnd(true);
    setVoucherStartDate("");
    setVoucherEndDate("");
    setCtaParameters({});
    setOtherBaseUrl("");
    setSelectedSalesPositionId("");
    setSelectedRepIds([]);
    setPerRepResults(null);
    setIncludeStateParam(false);
    setUseStateDefaults(false);
    setSingleVoucher(false);
    setUseMemberSignup(false);
    setDisplayAwayPlans(false);
  };

  // Initialize form defaults when opening the add CTA form
  const openAddCTAForm = () => {
    // Default voucher dates to event dates
    const startDate = getDatePart(event.start_time);
    const endDate = getDatePart(event.end_time);
    if (startDate && startDate !== "9999-12-31") {
      setVoucherStartDate(startDate);
    }
    if (endDate) {
      setVoucherEndDate(endDate);
    }

    // Prepopulate CTA parameters from promoter org
    const params: Record<string, string> = {};
    if (promoterPromoCode) {
      params.promo = promoterPromoCode;
    }
    if (promoterAffiliateId) {
      params.affid = promoterAffiliateId;
    }
    setCtaParameters(params);

    setShowAddCTA(true);
  };

  // Update parameter value
  const handleParameterChange = (key: string, value: string) => {
    setCtaParameters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleCreateCTA = async () => {
    if (!newCTAType) {
      showToast("error", "Please select a CTA type");
      return;
    }

    // Validate base URL for "other" type
    if (newCTAType === "other" && !otherBaseUrl.trim()) {
      showToast("error", "Please enter a base URL for the CTA");
      return;
    }

    // Sprint A Batch 3 Ship B: 'none' = attribution-only CTA (rep gets
    // credit via sales_position_id; no voucher/offer). Skip voucher
    // and offer validation entirely.
    // Validate voucher fields if reward type is voucher
    if (rewardType === "voucher" && !noVoucherHere) {
      if (!voucherAmount || parseFloat(voucherAmount) <= 0) {
        showToast("error", "Please enter a valid voucher amount");
        return;
      }
      const overCeiling =
        newCTAType === "subscription"
          ? ceilingProblem(stateOffer, parseFloat(voucherAmount))
          : null;
      if (overCeiling) {
        showToast("error", overCeiling);
        return;
      }
    }

    // Validate offer selection if reward type is offer
    if (rewardType === "offer" && !selectedOfferId) {
      showToast("error", "Please select an offer");
      return;
    }

    try {
      // Build parameters object - start with user-entered params
      const finalParameters: Record<string, string> = { ...ctaParameters };

      // Auto-populate from promoter if not already set
      if (promoterPromoCode && !finalParameters.promo) {
        finalParameters.promo = promoterPromoCode;
      }
      if (promoterAffiliateId && !finalParameters.affid) {
        finalParameters.affid = promoterAffiliateId;
      }

      // Filter out empty values
      Object.keys(finalParameters).forEach((key) => {
        if (!finalParameters[key]?.trim()) {
          delete finalParameters[key];
        }
      });

      // Default name to CTA type if not provided
      const baseCtaName =
        newCTAName.trim() ||
        newCTAType.charAt(0).toUpperCase() + newCTAType.slice(1);

      // R2B (2026-05-17): multi-rep creation per audit Decision 5.
      // If the operator selected N reps via the multi-select, create N
      // CTAs (one per rep), each with their own QR. If they used the
      // legacy single-select or none, create one CTA. Sequential creation
      // with per-row result reporting per audit must-fix on partial
      // failures.
      const repsToCreate: { repId: string | null; suffix: string }[] =
        selectedRepIds.length > 0
          ? selectedRepIds.map((id) => {
              const pos = salesPositions?.find(
                (p: SalesPosition) => p.id === id,
              );
              const repLabel = pos?.assigned_name || pos?.title || id;
              return { repId: id, suffix: ` — ${repLabel}` };
            })
          : [{ repId: selectedSalesPositionId || null, suffix: "" }];

      const buildInput = (repId: string | null, nameSuffix: string) => ({
        event_id: event.id,
        name: `${baseCtaName}${nameSuffix}`,
        cta_type: newCTAType,
        base_url:
          newCTAType === "other"
            ? otherBaseUrl.trim()
            : getDefaultBaseUrl(newCTAType),
        parameters: finalParameters,
        // R2 (Sprint A Batch 3 Ship B, auditor should-fix): send explicit
        // null instead of undefined for cleared reward fields. undefined
        // means "omit this field" which can leave stale values on edit
        // paths or if a reused input is later applied to edit mode.
        reward_type: rewardType,
        offer_id: rewardType === "offer" ? selectedOfferId : null,
        voucher_amount:
          rewardType === "voucher" && voucherAmount
            ? parseFloat(voucherAmount)
            : null,
        voucher_expiry_days:
          rewardType === "voucher" &&
          !voucherExpiresAtEventEnd &&
          voucherExpiryDays
            ? parseInt(voucherExpiryDays, 10)
            : null,
        voucher_expires_at_event_end:
          rewardType === "voucher" ? voucherExpiresAtEventEnd : false,
        voucher_start_date:
          rewardType === "voucher" && voucherStartDate
            ? voucherStartDate
            : null,
        voucher_end_date:
          rewardType === "voucher" && voucherEndDate ? voucherEndDate : null,
        sales_position_id: repId ?? undefined,
        include_state_param:
          newCTAType === "subscription" ? includeStateParam : undefined,
        use_state_defaults:
          newCTAType === "subscription" ? useStateDefaults : undefined,
        single_voucher:
          newCTAType === "subscription" &&
          (rewardType === "voucher" || rewardType === "offer")
            ? singleVoucher
            : undefined,
        use_member_signup:
          newCTAType === "subscription" ? useMemberSignup : undefined,
        display_away_plans:
          newCTAType === "subscription" ? displayAwayPlans : undefined,
      });

      const results: typeof perRepResults = [];
      for (const { repId, suffix } of repsToCreate) {
        const repLabel = repId
          ? (salesPositions?.find((p: SalesPosition) => p.id === repId)
              ?.assigned_name ??
            salesPositions?.find((p: SalesPosition) => p.id === repId)?.title ??
            "Unassigned")
          : "Unassigned";
        try {
          const created = await createCTA.mutateAsync(
            buildInput(repId, suffix),
          );
          // Best-effort QR generation (the CTA already exists; a QR failure is
          // non-fatal and recoverable via "Regenerate QRs", mirroring the clone
          // path's partial-success design).
          try {
            await generateQR.mutateAsync({ ctaId: created.id, event });
          } catch (qrErr) {
            console.warn(
              "CTA created but QR generation failed (use Regenerate QRs):",
              qrErr,
            );
          }
          results!.push({ repId: repId ?? "", repName: repLabel, ok: true });
        } catch (err) {
          results!.push({
            repId: repId ?? "",
            repName: repLabel,
            ok: false,
            error: err instanceof Error ? err.message : "Unknown error",
          });
        }
      }

      const okCount = results!.filter((r) => r.ok).length;
      const failCount = results!.length - okCount;
      if (failCount === 0) {
        showToast(
          "success",
          `Created ${okCount} CTA${okCount === 1 ? "" : "s"}`,
        );
        resetForm();
      } else {
        // Surface partial failures in-form per audit must-fix
        setPerRepResults(results);
        showToast(
          "error",
          `Created ${okCount} of ${results!.length} CTAs. See per-rep results below.`,
        );
      }
      refetch();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create CTA";
      showToast("error", errorMessage);
    }
  };

  if (isLoading || isLoadingAccess) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-semibold">Error loading CTAs</h3>
        <p className="text-red-600 text-sm mt-1">
          {error instanceof Error ? error.message : "An error occurred"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Calls to Action
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Create marketing URLs with QR codes for tracking and reward
            distribution
          </p>
        </div>
        {canEdit && !showAddCTA && (
          <Button
            variant="primary"
            size="sm"
            icon={PlusIcon}
            onClick={openAddCTAForm}
          >
            Add CTA
          </Button>
        )}
      </div>

      {/* State Warning */}
      {!hasRequiredState && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800 font-medium">
              Regulatory State Required
            </p>
            <p className="text-sm text-amber-700 mt-1">
              This event needs a regulatory state set before Subscription CTAs
              can generate proper URLs. Edit the event to set the state.
            </p>
          </div>
        </div>
      )}

      {/* Prepare the event's subscription CTA from its promoter/host -- the same panel as the create
          wizard's last step. It renders nothing when there is nothing to do. Hidden once the event has
          ended: a QR code for a finished event helps no one, and existing events are not a backfill
          target (docs/PLAN_EIB_DEFAULT_CTA_2026-09-25.md). */}
      {canEdit && (
        <EventCTAInitPanel
          event={event}
          surface="tab"
          allowPrepare={!eventEnded}
        />
      )}

      {/* Add CTA Form */}
      {showAddCTA && canEdit && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="font-medium text-gray-900 mb-4">New Call to Action</h4>

          <div className="space-y-4">
            {/* CTA Type - shown first so name can default from type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-4">
                <button
                  type="button"
                  onClick={() => setNewCTAType("subscription")}
                  className={`
                    p-4 border rounded-lg text-left transition-colors
                    ${
                      newCTAType === "subscription"
                        ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500"
                        : "border-gray-300 hover:border-gray-400"
                    }
                  `}
                >
                  <div className="font-medium text-gray-900">Subscription</div>
                  <p className="text-sm text-gray-500 mt-1">
                    U.S. Law Shield voucher signup
                  </p>
                  <p className="text-xs text-gray-400 mt-2 truncate">
                    {SUBSCRIPTION_CTA_BASE_URL}
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setNewCTAType("registration")}
                  className={`
                    p-4 border rounded-lg text-left transition-colors
                    ${
                      newCTAType === "registration"
                        ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500"
                        : "border-gray-300 hover:border-gray-400"
                    }
                  `}
                >
                  <div className="font-medium text-gray-900">Registration</div>
                  <p className="text-sm text-gray-500 mt-1">
                    Event registration form
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Attendee signup page
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setNewCTAType("other")}
                  className={`
                    p-4 border rounded-lg text-left transition-colors
                    ${
                      newCTAType === "other"
                        ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500"
                        : "border-gray-300 hover:border-gray-400"
                    }
                  `}
                >
                  <div className="font-medium text-gray-900">Other</div>
                  <p className="text-sm text-gray-500 mt-1">
                    Custom URL with parameters
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    You define the base URL
                  </p>
                </button>
              </div>
            </div>

            {/* CTA Name - optional, defaults to CTA type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={newCTAName}
                onChange={(e) => setNewCTAName(e.target.value)}
                placeholder={
                  newCTAType
                    ? `${newCTAType.charAt(0).toUpperCase() + newCTAType.slice(1)} (default)`
                    : "e.g., Control Group, Test A"
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Optional label to identify this CTA (for A/B testing or campaign
                tracking). Defaults to the CTA type.
              </p>
            </div>

            {/* Credited Rep Attribution — R2B (2026-05-17).
                Multi-select per audit Decision 5: each selected rep gets
                their own CTA with their own QR. Leave empty to create
                a single generic CTA with no rep attribution (the
                "fallback" pattern for last-minute coverage). */}
            {salesPositions && salesPositions.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Credited Reps (one CTA per rep)
                </label>
                <select
                  multiple
                  value={selectedRepIds}
                  onChange={(e) =>
                    setSelectedRepIds(
                      Array.from(e.target.selectedOptions, (opt) => opt.value),
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 min-h-[120px]"
                  size={Math.min(
                    8,
                    Math.max(4, (salesPositions?.length ?? 0) + 1),
                  )}
                >
                  {salesPositions.map((pos: SalesPosition) => {
                    const inactive =
                      (pos as { is_active?: boolean }).is_active === false;
                    return (
                      <option
                        key={pos.id}
                        value={pos.id}
                        className={inactive ? "text-gray-500" : ""}
                      >
                        {pos.assigned_name || pos.title}
                        {pos.assigned_name && pos.title !== pos.assigned_name
                          ? ` (${pos.title})`
                          : ""}
                        {inactive ? " — inactive" : ""}
                      </option>
                    );
                  })}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Cmd/Ctrl-click to select multiple reps. Each will get their
                  own CTA + unique QR code showing their name and the event.
                  Leave empty to create a single generic CTA with no rep
                  attribution (use for last-minute coverage or
                  affiliate-partner-level codes).
                </p>
                {perRepResults && perRepResults.length > 0 && (
                  <div className="mt-2 border border-gray-200 rounded-md divide-y">
                    {perRepResults.map((r, i) => (
                      <div
                        key={`${r.repId}-${i}`}
                        className={`px-3 py-2 text-xs ${r.ok ? "bg-green-50 text-green-900" : "bg-red-50 text-red-900"}`}
                      >
                        <strong>{r.repName}</strong>:{" "}
                        {r.ok ? "created" : `failed — ${r.error}`}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Reward Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reward Type <span className="text-red-500">*</span>
              </label>
              {noVoucherHere && (
                <p
                  className="mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2"
                  data-testid="cta-no-voucher-note"
                >
                  {INSURANCE_NO_VOUCHER_NOTE}
                </p>
              )}
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  disabled={noVoucherHere}
                  title={noVoucherHere ? INSURANCE_NO_VOUCHER_NOTE : undefined}
                  onClick={() => {
                    setRewardType("voucher");
                    setSelectedOfferId("");
                  }}
                  className={`
                    p-3 border rounded-lg text-center transition-colors
                    ${noVoucherHere ? "opacity-40 cursor-not-allowed" : ""}
                    ${
                      rewardType === "voucher"
                        ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500"
                        : "border-gray-300 hover:border-gray-400"
                    }
                  `}
                >
                  <div className="font-medium text-gray-900 text-sm">
                    Voucher
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Generate discount code
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRewardType("offer");
                    setVoucherAmount("");
                    setVoucherStartDate("");
                    setVoucherEndDate("");
                  }}
                  className={`
                    p-3 border rounded-lg text-center transition-colors
                    ${
                      rewardType === "offer"
                        ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500"
                        : "border-gray-300 hover:border-gray-400"
                    }
                  `}
                >
                  <div className="font-medium text-gray-900 text-sm">Offer</div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Link to existing offer
                  </p>
                </button>
                {/* Sprint A Batch 3 Ship B (2026-06-06): attribution-only
                    CTA — rep gets credit via sales_position_id, no voucher
                    or offer attached. Foundational for the future MSR
                    portal. */}
                <button
                  type="button"
                  onClick={() => {
                    setRewardType("none");
                    setSelectedOfferId("");
                    setVoucherAmount("");
                    setVoucherStartDate("");
                    setVoucherEndDate("");
                  }}
                  className={`
                    p-3 border rounded-lg text-center transition-colors
                    ${
                      rewardType === "none"
                        ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500"
                        : "border-gray-300 hover:border-gray-400"
                    }
                  `}
                >
                  <div className="font-medium text-gray-900 text-sm">None</div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Attribution only
                  </p>
                </button>
              </div>
            </div>

            {/* Voucher Configuration (shown when reward type is voucher) */}
            {rewardType === "voucher" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <h5 className="text-sm font-medium text-blue-800">
                  Voucher Configuration
                </h5>

                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={voucherExpiresAtEventEnd}
                    onChange={(e) =>
                      setVoucherExpiresAtEventEnd(e.target.checked)
                    }
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Expire voucher at the end of the event
                </label>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        $
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={voucherAmount}
                        onChange={(e) => setVoucherAmount(e.target.value)}
                        placeholder={
                          stateCeiling !== null ? String(stateCeiling) : "20.00"
                        }
                        max={
                          newCTAType === "subscription" && stateCeiling !== null
                            ? stateCeiling
                            : undefined
                        }
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    {newCTAType === "subscription" && stateCeiling !== null && (
                      <p
                        className="text-xs text-gray-500 mt-1"
                        data-testid="cta-add-state-ceiling"
                      >
                        Up to ${stateCeiling}: the Event in a Box offer for{" "}
                        {stateOffer?.state ?? "this state"}. You can give less,
                        not more.
                      </p>
                    )}
                  </div>
                  {!voucherExpiresAtEventEnd && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expiry Days <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={voucherExpiryDays}
                        onChange={(e) => setVoucherExpiryDays(e.target.value)}
                        placeholder="30"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      />
                      <p className="text-xs text-gray-500 mt-0.5">
                        Days from creation until expiry
                      </p>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valid From
                    </label>
                    <input
                      type="date"
                      value={voucherStartDate}
                      onChange={(e) => setVoucherStartDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valid Until
                    </label>
                    <input
                      type="date"
                      value={voucherEndDate}
                      onChange={(e) => setVoucherEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <p className="text-xs text-blue-700">
                  Dates use event timezone (
                  {US_TIMEZONES.find(
                    (tz) => tz.value === (event.timezone || DEFAULT_TIMEZONE),
                  )?.label || "Eastern"}{" "}
                  -{" "}
                  {US_TIMEZONES.find(
                    (tz) => tz.value === (event.timezone || DEFAULT_TIMEZONE),
                  )?.abbr || "EST"}
                  ). Vouchers will be generated when users complete the CTA.
                  Each voucher gets a unique code.
                </p>
              </div>
            )}

            {/* Offer Selection (shown when reward type is offer) */}
            {rewardType === "offer" && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                <h5 className="text-sm font-medium text-green-800">
                  Select Offer
                </h5>

                {isLoadingOffers ? (
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <LoadingSpinner size="small" />
                    <span>Loading offers...</span>
                  </div>
                ) : availableOffers && availableOffers.length > 0 ? (
                  <div>
                    <select
                      value={selectedOfferId}
                      onChange={(e) => setSelectedOfferId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select an offer...</option>
                      {availableOffers.map((offer) => (
                        <option key={offer.id} value={offer.id}>
                          {offer.name} ({offer.offer_code})
                          {offer.value_amount
                            ? ` - $${offer.value_amount}`
                            : ""}
                        </option>
                      ))}
                    </select>

                    {selectedOfferId && (
                      <div className="mt-2 p-2 bg-white rounded border border-green-200">
                        {(() => {
                          const selected = availableOffers.find(
                            (o) => o.id === selectedOfferId,
                          );
                          if (!selected) {
                            return null;
                          }
                          return (
                            <div className="text-sm">
                              <p className="font-medium text-gray-900">
                                {selected.name}
                              </p>
                              <p className="text-gray-500">
                                Code: {selected.offer_code}
                                {selected.value_amount && (
                                  <span className="ml-2">
                                    Value: ${selected.value_amount}
                                  </span>
                                )}
                              </p>
                              {selected.description && (
                                <p className="text-gray-500 text-xs mt-1">
                                  {selected.description}
                                </p>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">
                    <p>No offers available.</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Create an offer in the Offers section first.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* URL Parameters Section - for Subscription CTA */}
            {newCTAType === "subscription" && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h5 className="text-sm font-medium text-gray-800">
                    URL Parameters
                  </h5>
                  <span className="text-xs text-gray-500">
                    Configure tracking and discount parameters
                  </span>
                </div>

                {/* Auto-populated fields info */}
                <div className="bg-blue-50 rounded-md p-3 text-sm text-blue-700">
                  <p className="font-medium mb-1">Auto-populated:</p>
                  <ul className="list-disc list-inside text-xs space-y-0.5">
                    <li
                      className={
                        !includeStateParam ? "line-through opacity-50" : ""
                      }
                    >
                      <code className="bg-blue-100 px-1 rounded">ste</code>:{" "}
                      {event.state || "(set in event details)"}
                    </li>
                    <li>
                      <code className="bg-blue-100 px-1 rounded">ctaid</code>:
                      (generated on save)
                    </li>
                    <li>
                      <code className="bg-blue-100 px-1 rounded">eventid</code>:{" "}
                      {event.id
                        ? event.id.substring(0, 8) + "..."
                        : "(set on save)"}
                    </li>
                    <li>
                      <code className="bg-blue-100 px-1 rounded">
                        discountvalue
                      </code>
                      : {voucherAmount || "(set voucher amount above)"}
                    </li>
                  </ul>
                </div>

                {/* Include state parameter checkbox */}
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeStateParam}
                    onChange={(e) => setIncludeStateParam(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Include state parameter (
                  <code className="text-xs bg-gray-100 px-1 rounded">ste=</code>
                  ) in URL
                </label>
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={useStateDefaults}
                    onChange={(e) => setUseStateDefaults(e.target.checked)}
                    className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>
                    Use State Defaults
                    <span className="block text-xs text-gray-500">
                      If the member picks a different state during signup, the
                      voucher amount switches to that state's default. Adds{" "}
                      <code className="bg-gray-100 px-1 rounded">
                        usestatedefaults=1
                      </code>{" "}
                      to the URL. Off by default, so existing CTAs and printed
                      QR codes are unchanged.
                    </span>
                  </span>
                </label>

                {(rewardType === "voucher" || rewardType === "offer") && (
                  <label className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      aria-label="Single voucher"
                      checked={singleVoucher}
                      onChange={(e) => setSingleVoucher(e.target.checked)}
                      className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      Single voucher
                      <span className="block text-xs text-gray-500">
                        Creates the voucher for the primary member only, not the
                        secondary. Adds{" "}
                        <code className="bg-gray-100 px-1 rounded">
                          singlevoucher=1
                        </code>{" "}
                        to the QR code and landing page URL. Off by default.
                      </span>
                    </span>
                  </label>
                )}

                {/* Use member signup URL checkbox */}
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useMemberSignup}
                    onChange={(e) => setUseMemberSignup(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Use member signup URL
                  <span className="text-xs text-gray-500">
                    Skips the voucher landing page and starts on plan selection
                    (unchecked = /voucher/, checked = /member-signup/)
                  </span>
                </label>

                {/* Display away plans checkbox */}
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={displayAwayPlans}
                    onChange={(e) => setDisplayAwayPlans(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Display away plans
                  <span className="text-xs text-gray-500">
                    (with member signup URL, starts on the Away plans screen;
                    adds plantype=away)
                  </span>
                </label>

                {/* Editable parameters */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {SUBSCRIPTION_FIELDS.map((field) => {
                    const isAutoPopulated =
                      (field.key === "promo" && promoterPromoCode) ||
                      (field.key === "affid" && promoterAffiliateId);
                    const autoLabel =
                      field.key === "promo" && promoterPromoCode
                        ? promoterPromoCode
                        : field.key === "affid" && promoterAffiliateId
                          ? promoterAffiliateId
                          : null;
                    return (
                      <div key={field.key}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {field.label}
                          {isAutoPopulated && (
                            <span className="ml-2 text-green-600 font-normal">
                              (auto: {autoLabel})
                            </span>
                          )}
                        </label>
                        <input
                          type="text"
                          value={ctaParameters[field.key] || ""}
                          onChange={(e) =>
                            handleParameterChange(field.key, e.target.value)
                          }
                          placeholder={
                            isAutoPopulated
                              ? (autoLabel as string)
                              : field.placeholder
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                        />
                        {field.key === "promo" && (
                          // Informational only -- never blocks the save (owner rule 2026-10-25).
                          // An empty field falls back to the promoter's code on save, so show that one.
                          <PromoCodeLookupStatus
                            code={
                              ctaParameters.promo || promoterPromoCode || ""
                            }
                            canRecheck={canEdit}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* URL Parameters Section - for Registration CTA */}
            {newCTAType === "registration" && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-4">
                <h5 className="text-sm font-medium text-green-800">
                  Event Registration Configuration
                </h5>

                <div className="bg-white rounded-md p-3 text-sm text-green-700">
                  <p className="font-medium mb-1">How it works:</p>
                  <ul className="list-disc list-inside text-xs space-y-0.5">
                    <li>QR code links to event registration page</li>
                    <li>Visitors can register with name and email</li>
                    <li>Registrants are added to event attendees list</li>
                    <li>Auto-generated segment includes all registrants</li>
                  </ul>
                </div>

                <div className="bg-white rounded-md p-3 text-sm">
                  <p className="font-medium text-gray-700 mb-1">
                    Auto-populated:
                  </p>
                  <ul className="list-disc list-inside text-xs text-gray-600 space-y-0.5">
                    <li>
                      <code className="bg-gray-100 px-1 rounded">event_id</code>
                      : {event.id.slice(0, 8)}...
                    </li>
                    <li>
                      <code className="bg-gray-100 px-1 rounded">ctaid</code>:
                      (generated on save)
                    </li>
                  </ul>
                </div>

                {/* UTM parameters for tracking */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tracking Parameters (optional)
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      {
                        key: "utm_source",
                        label: "UTM Source",
                        placeholder: "e.g., flyer",
                      },
                      {
                        key: "utm_medium",
                        label: "UTM Medium",
                        placeholder: "e.g., qrcode",
                      },
                    ].map((field) => (
                      <div key={field.key}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {field.label}
                        </label>
                        <input
                          type="text"
                          value={ctaParameters[field.key] || ""}
                          onChange={(e) =>
                            handleParameterChange(field.key, e.target.value)
                          }
                          placeholder={field.placeholder}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* URL Parameters Section - for Other CTA */}
            {newCTAType === "other" && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                <h5 className="text-sm font-medium text-gray-800">
                  Custom URL Configuration
                </h5>

                {/* Base URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Base URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    value={otherBaseUrl}
                    onChange={(e) => setOtherBaseUrl(e.target.value)}
                    placeholder="https://example.com/landing-page"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    The base URL where users will be directed
                  </p>
                </div>

                {/* Common parameters for other type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL Parameters (optional)
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      {
                        key: "utm_source",
                        label: "UTM Source",
                        placeholder: "e.g., event",
                      },
                      {
                        key: "utm_medium",
                        label: "UTM Medium",
                        placeholder: "e.g., qrcode",
                      },
                      {
                        key: "utm_campaign",
                        label: "UTM Campaign",
                        placeholder: "e.g., spring_promo",
                      },
                      {
                        key: "utm_term",
                        label: "UTM Term",
                        placeholder: "e.g., signup",
                      },
                    ].map((field) => (
                      <div key={field.key}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {field.label}
                        </label>
                        <input
                          type="text"
                          value={ctaParameters[field.key] || ""}
                          onChange={(e) =>
                            handleParameterChange(field.key, e.target.value)
                          }
                          placeholder={field.placeholder}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={resetForm}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateCTA}
                disabled={createCTA.isPending || !newCTAType}
              >
                {createCTA.isPending ? "Creating..." : "Create CTA"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* CTAs List */}
      {ctas && ctas.length > 0 ? (
        <div className="space-y-4">
          {ctas.map((cta) => (
            <CTACard
              key={cta.id}
              cta={cta}
              event={event}
              canEdit={canEdit}
              utmTermDerived={
                prepared?.status === "initialized" &&
                prepared.initialization?.event_cta_id === cta.id
              }
              onUpdate={refetch}
            />
          ))}
        </div>
      ) : (
        !showAddCTA && (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <QrCodeIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              No Call to Actions
            </h4>
            <p className="text-sm text-gray-500 mb-4">
              Create CTAs with QR codes to track marketing campaigns and event
              signups
            </p>
            {canEdit && (
              <Button
                variant="primary"
                icon={PlusIcon}
                onClick={openAddCTAForm}
              >
                Add First CTA
              </Button>
            )}
          </div>
        )
      )}

      {/* Info Box */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          About Calls to Action
        </h4>
        <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
          <li>
            <strong>Subscription:</strong> Uses the standard U.S. Law Shield
            voucher URL with affiliate tracking
          </li>
          <li>
            <strong>Registration:</strong> Event registration form for attendee
            signups with automatic segment creation
          </li>
          <li>
            <strong>Other:</strong> Create custom URLs with any parameters for
            external landing pages
          </li>
          <li>
            <strong>Voucher:</strong> Generate discount codes with configurable
            amounts and validity dates
          </li>
          <li>
            <strong>Offer:</strong> Link to an existing offer from your offers
            library
          </li>
          <li>Generate QR codes to include in marketing materials</li>
          <li>Create multiple CTAs for A/B testing different campaigns</li>
        </ul>
      </div>
    </div>
  );
};

export default CallToActionTab;
